import { useContext, useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import SoftBox from "components/SoftBox";
import Avatar from "@mui/material/Avatar";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import DataTable from "cxfcomponents/Tables/DataTable";
import StatusBadge from "cxfcomponents/StatusBadge";
import SoftTypography from "components/SoftTypography";
import { useUsers } from "hooks/useUsers";
import { useRoles } from "hooks/useRoles";
import { useAuth0 } from "@auth0/auth0-react";
import { ThemedMailIcon } from "assets/CXFIcons/Static";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import { PERMISSIONS } from "constants/portal";
import { toast } from "react-toastify";
import { RemoveUserDialog } from "../../../removeUser";
import { ConfirmRoleChangeDialog } from "../../../confirmChange";
import UserRole from "./Role";
import { updateUserRoles } from "api/RolesApi";
import AssignRoleModal from "layouts/Flows/components/UsersModal/index4";
import { ConfirmAssignRoleDialog } from "../../../confirmAssignRoleDialog";

const Users = () => {
	const { roleId } = useParams();
	const { user } = useAuth0();
	const [searchName, setSearchName] = useState("");
	const { users, handleFetchUsers } = useUsers(false);
	const { roles } = useRoles(false);
	const [userToBeRemoved, setUserToBeRemoved] = useState(null);
	const { userPermissions } = useContext(PermissionsContext);
	const [selectedUser, setSelectedUser] = useState(null);
	const [newSelectedRole, setNewSelectedRole] = useState(null);
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [openAssignRoleModal, setOpenAssignRoleModal] = useState(false);
	const [showAssignConfirmDialog, setShowAssignConfirmDialog] =
		useState(false);
	const [conflictingUsers, setConflictingUsers] = useState([]);
	const [pendingSelectedUserIds, setPendingSelectedUserIds] = useState([]);

	const ownerRoleId = useMemo(() => {
		if (!Array.isArray(roles)) return null;
		const ownerRole = roles.find(
			(r) => r.name?.trim().toLowerCase() === "owner"
		);
		return ownerRole ? ownerRole.id : null;
	}, [roles]);

	const currentRoleName = useMemo(() => {
		if (!roles || !roleId) return "";
		return roles.find((r) => r.id === roleId)?.name || "";
	}, [roles, roleId]);

	const usersWithoutRole = useMemo(() => {
		if (
			!Array.isArray(users) ||
			!roleId ||
			!Array.isArray(roles) ||
			ownerRoleId === undefined
		) {
			return [];
		}

		const targetRoleId = roleId;

		return users.filter((u) => {
			const isOwner = ownerRoleId !== null && u.role_id === ownerRoleId;
			if (isOwner) {
				return false;
			}

			const hasTargetRole = u.role_id === targetRoleId;
			if (hasTargetRole) {
				return false;
			}

			return true;
		});
	}, [users, roleId, roles, ownerRoleId]);

	useEffect(() => {
		if (!users || users.length === 0) {
			handleFetchUsers();
		}
	}, [handleFetchUsers, users]);

	const handleOpenConfirmDialog = (user, newRole) => {
		setSelectedUser(user);
		setNewSelectedRole(newRole);
		setConfirmDialogOpen(true);
	};

	const handleOpenAddRoleModal = () => {
		setOpenAssignRoleModal(true);
	};

	const handleCloseAssignRoleModal = () => {
		setOpenAssignRoleModal(false);
	};

	const performRoleAssignment = async (userIdsToAssign) => {
		if (!roleId) {
			toast.error("Cannot assign users: Role ID is missing.");
			return;
		}
		if (!user?.stripeId) {
			toast.error("Cannot assign users: Tenant ID is missing.");
			return;
		}

		let successCount = 0;
		let errorCount = 0;

		await Promise.all(
			userIdsToAssign.map(async (userId) => {
				try {
					const response = await updateUserRoles(
						user.stripeId,
						userId,
						roleId
					);
					if (response?.isSuccessful) {
						console.log(`Successfully assigned role to user ${userId}`);
						successCount++;
					} else {
						console.error(
							`Failed to assign role to user ${userId}:`,
							response?.message
						);
						errorCount++;
					}
				} catch (error) {
					console.error(`Error assigning role to user ${userId}:`, error);
					errorCount++;
				}
			})
		);

		if (successCount > 0) {
			toast.success(
				`${successCount} user(s) assigned to the role successfully.`
			);
		}
		if (errorCount > 0) {
			toast.error(
				`Failed to assign role for ${errorCount} user(s). Check console.`
			);
		}

		await handleFetchUsers();
	};

	const handleAddUsersToRole = async (selectedUserIds) => {
		if (!roleId || !users || !roles) {
			toast.error("Cannot assign users: Missing required data.");
			return;
		}

		const selectedUsersFull = users.filter((u) =>
			selectedUserIds.includes(u.auth0_id)
		);

		const usersWithConflicts = selectedUsersFull.filter(
			(u) => u.role_id && u.role_id !== roleId && u.role_id !== ownerRoleId
		);

		if (usersWithConflicts.length > 0) {
			const conflictDetails = usersWithConflicts.map((u) => {
				const currentRole = roles.find((r) => r.id === u.role_id);
				return {
					name:
						`${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() ||
						u.email_address,
					currentRoleName: currentRole?.name || "Unknown Role"
				};
			});

			setConflictingUsers(conflictDetails);
			setPendingSelectedUserIds(selectedUserIds);
			setShowAssignConfirmDialog(true);
			handleCloseAssignRoleModal();
		} else {
			await performRoleAssignment(selectedUserIds);
			handleCloseAssignRoleModal();
		}
	};

	const handleConfirmAssignAndOverwrite = async () => {
		setShowAssignConfirmDialog(false);
		await performRoleAssignment(pendingSelectedUserIds);
		setPendingSelectedUserIds([]);
		setConflictingUsers([]);
	};

	const handleCancelAssignAndOverwrite = () => {
		setShowAssignConfirmDialog(false);
		setPendingSelectedUserIds([]);
		setConflictingUsers([]);
	};

	const handleConfirmRoleChange = async () => {
		const userId = selectedUser?.auth0_id;
		const newRoleIdValue = newSelectedRole?.value;

		if (!userId || !newRoleIdValue) {
			console.error("User ID or New Role ID is missing for update.");
			toast.error("Cannot update role.");
			setConfirmDialogOpen(false);
			return;
		}

		try {
			const response = await updateUserRoles(
				user?.stripeId,
				userId,
				newRoleIdValue
			);

			if (response?.isSuccessful) {
				toast.success(`Role updated successfully`);
				await handleFetchUsers();
			} else {
				toast.error(response?.message || "Failed to update role.");
			}
		} catch (error) {
			console.error("Failed to update role:", error);
			toast.error("Failed to update role. See console for details.");
		} finally {
			setConfirmDialogOpen(false);
			setSelectedUser(null);
			setNewSelectedRole(null);
		}
	};

	const handleCancelRoleChange = () => {
		setConfirmDialogOpen(false);
		setSelectedUser(null);
		setNewSelectedRole(null);
	};

	const handleSetUserToBeRemoved = (user) => {
		setUserToBeRemoved(user);
	};

	const handleRemoveUser = async () => {
		const userId = userToBeRemoved?.auth0_id;
		if (!userId) {
			console.error("User ID is missing for removal.");
			toast.error("Cannot remove user role. Missing information.");
			setUserToBeRemoved(null);
			return;
		}

		try {
			const response = await updateUserRoles(user?.stripeId, userId, null);

			if (response?.isSuccessful) {
				toast.success("User role removed");
				await handleFetchUsers();
			} else {
				toast.error(response?.message || "Failed to remove user role.");
			}
		} catch (error) {
			console.error("Failed to remove user role:", error);
			toast.error("Failed to remove user role. See console for details.");
		} finally {
			setUserToBeRemoved(null);
		}
	};

	const isAllowedAction = (action) => {
		if (
			userPermissions.some(
				(permission) => permission.name === "owner" && permission.value
			)
		) {
			return true;
		}

		switch (action) {
			case "assign":
				return userPermissions.some(
					(permission) =>
						permission.name === PERMISSIONS.ASSIGN_ROLE &&
						permission.value
				);
			case "remove":
				return userPermissions.some(
					(permission) =>
						permission.name === PERMISSIONS.REMOVE_USERS &&
						permission.value
				);
			case "edit":
				return userPermissions.some(
					(permission) =>
						permission.name === PERMISSIONS.EDIT_ROLES && permission.value
				);
			case "create":
				return userPermissions.some(
					(permission) =>
						permission.name === PERMISSIONS.CREATE_ROLES &&
						permission.value
				);
			default:
				return false;
		}
	};

	const getUsersForTable = useMemo(() => {
		if (!users || !roleId) return [];

		const roleFilteredUsers = users.filter((user) => user.role_id === roleId);

		if (!searchName) return roleFilteredUsers;

		const lowerCaseSearch = searchName.toLowerCase();
		return roleFilteredUsers.filter(
			(user) =>
				(user.first_name?.toLowerCase() || "").includes(lowerCaseSearch) ||
				(user.last_name?.toLowerCase() || "").includes(lowerCaseSearch) ||
				(user.email_address?.toLowerCase() || "").includes(lowerCaseSearch)
		);
	}, [users, searchName, roleId]);

	const tableData = useMemo(() => {
		if (!roles || roles.length === 0 || !users) {
			return { columns: [], rows: [] };
		}

		return {
			columns: [
				{
					Header: "profile",
					accessor: "profile",
					width: 96,
					Cell: ({ value, row }) => (
						<Avatar
						src={value || undefined}
							sx={{
								border: "1px solid var(--portal-surface-border)",
								borderRadius: "12px",
								width: "60px",
								height: "60px"
							}}
						>
							{`${row.original.first_name?.[0] || ""}${row.original.last_name?.[0] || ""}`.toUpperCase()}
						</Avatar>
					)
				},
				{
					Header: "name",
					accessor: "name",
					width: 250,
					Cell: ({ value }) => (
						<SoftTypography
							fontWeight="bold"
							fontSize="14px"
						>
							{" "}
							{value}{" "}
						</SoftTypography>
					)
				},
				{
					Header: "email",
					accessor: "email",
					width: 220,
					Cell: ({ value }) => (
						<SoftTypography
							color="secondary"
							sx={{
								fontWeight: 400,
								fontSize: "14px",
								display: "flex",
								alignItems: "center"
							}}
						>
							<ThemedMailIcon
								width="19px"
								height="19px"
								sx={{ marginRight: "8px" }}
							/>{" "}
							{value}
						</SoftTypography>
					)
				},
				{
					Header: "status",
					accessor: "status",
					width: 180,
					Cell: ({ value }) => <StatusBadge status={value} />
				},
				{
					Header: "role",
					accessor: "user",
					style: { width: "100% " },
					Cell: ({ row }) => (
						<UserRole
							user={row.original.user}
							roles={roles}
							isAllowedAction={isAllowedAction}
							onOpenConfirmDialog={handleOpenConfirmDialog}
							onSetUserToBeRemoved={handleSetUserToBeRemoved}
						/>
					)
				}
			],
			rows: getUsersForTable.map((user) => {
				return {
					profile: user.image,
					name: `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || user.email_address || "User",
					email: user.email_address,
					status: user.status,
					user: user,
					auth0_id: user.auth0_id,
					first_name: user.first_name,
					last_name: user.last_name
				};
			})
		};
	}, [getUsersForTable, roles, isAllowedAction]);

	return (
		<>
			<SoftBox className="role-users-table">
				<SoftBox
					sx={{
						alignItems: "center",
						display: "flex",
						flexWrap: "wrap",
						gap: "12px",
						justifyContent: "flex-start",
						padding: "12px 40px 16px"
					}}
				>
					<SoftInput
						size="medium"
						placeholder="Search..."
						value={searchName}
						onChange={({ currentTarget }) =>
							setSearchName(currentTarget.value)
						}
						sx={{ width: { xs: "100%", sm: "380px" }, maxWidth: "380px" }}
					/>
					<SoftButton
						variant="outlined"
						color="info"
						onClick={handleOpenAddRoleModal}
						disabled={!isAllowedAction("assign")}
					>
						Add
					</SoftButton>
				</SoftBox>

				<DataTable
					table={tableData}
					entriesPerPage={{ defaultValue: 25 }}
					hiddenTopButtons={true}
					noTopPart
					tableHeight="clamp(350px, calc(100vh - 130px), calc(100vh - 520px))"
				/>
			</SoftBox>

			{userToBeRemoved && (
				<RemoveUserDialog
					isOpen={!!userToBeRemoved}
					user={userToBeRemoved}
					onClose={() => setUserToBeRemoved(null)}
					onAccept={handleRemoveUser}
				/>
			)}
			{confirmDialogOpen && (
				<ConfirmRoleChangeDialog
					isOpen={confirmDialogOpen}
					user={selectedUser}
					newRole={newSelectedRole}
					onClose={handleCancelRoleChange}
					onAccept={handleConfirmRoleChange}
				/>
			)}

			{openAssignRoleModal && (
				<AssignRoleModal
					open={openAssignRoleModal}
					onClose={handleCloseAssignRoleModal}
					usersToDisplay={usersWithoutRole}
					roleName={currentRoleName}
					onAssign={handleAddUsersToRole}
				/>
			)}

			{showAssignConfirmDialog && (
				<ConfirmAssignRoleDialog
					isOpen={showAssignConfirmDialog}
					onClose={handleCancelAssignAndOverwrite}
					onConfirm={handleConfirmAssignAndOverwrite}
					usersWithExistingRoles={conflictingUsers}
					targetRoleName={currentRoleName}
				/>
			)}
		</>
	);
};

export default Users;
